// Store
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetSearchInfo } from "store/slice/actions";
import { customAsyncThunk } from "store/slice/slices.functions";

// Store
import { RootState } from "app/store";

// Types
import { RequestInfo } from "store/slice/store.types";
import { ProjectPersonSet } from "services/projects/project.types";
import { fetchProjectByIdService } from "services/projects/project.service";
import { differenceBy } from "lodash";

type TPeopleSetSlice = {
    status: RequestInfo;
    data: ProjectPersonSet[];
};

const initialState: TPeopleSetSlice = {
    status: "pristine",
    data: [] as ProjectPersonSet[],
};

export const fetchPersonSet = customAsyncThunk<ProjectPersonSet, { projectId: number | string; path: string }>(
    "project/peopleSetList/getPersonSet",
    async ({ projectId, path }, { getState }) => {
        const {
            project: {
                peopleSet: { data: peopleSetList },
            },
        } = getState();

        const peopleSet = peopleSetList.find((set) => set.meta.path === path);

        if (peopleSet) {
            return peopleSet;
        } else {
            const { data } = await fetchProjectByIdService<ProjectPersonSet>(Number(projectId), path, {
                exclude_clones: false,
                exclude_person_set_persons: false,
            });
            return data;
        }
    }
);

const peopleSetSlice = createSlice({
    name: "project/peopleSetList",
    initialState,
    reducers: {
        addPersonSet: (state, action: PayloadAction<ProjectPersonSet[] | undefined>) => {
            const existingPeopleSet = state.data;
            const newSets = action.payload || [];
            const newValues = differenceBy(newSets, existingPeopleSet, "data.id");

            return { ...state, data: [...state.data, ...newValues] };
        },
        updatePersonSet: (state, action: PayloadAction<ProjectPersonSet>) => {
            const id = action.payload.data.id;
            const peopleSets = state.data.map((personSet) => {
                return personSet.data.id === id ? action.payload : personSet;
            });
            return { ...state, data: peopleSets };
        },
        clearInsightsData: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPersonSet.pending, (state) => ({ ...state, status: "fetching" }));
        builder.addCase(fetchPersonSet.rejected, (state) => ({ ...state, status: "error" }));
        builder.addCase(fetchPersonSet.fulfilled, (state, action) => ({
            ...state,
            data: state.data.find((d) => d.meta.path === action.payload.meta.path)
                ? state.data
                : [...state.data, action.payload],
            status: "pristine",
        }));
        builder.addCase(resetSearchInfo, () => initialState);
    },
});

export const { addPersonSet, updatePersonSet } = peopleSetSlice.actions;

export const getPeopleSets = (store: RootState) => store.project.peopleSet;
export const getPersonSetByPath = (personSetPath?: string) => (store: RootState) =>
    store.project.peopleSet.data.find((personSet) => personSet.meta.path === personSetPath);

export default peopleSetSlice.reducer;
